import { apiRequest } from "./api";
import { getOcpBaseUrl } from "./init";

export interface Net {
    id: number;
    name: string;
    slug: string;
    solution: string;
    mac?: string;
    ip: string;
    port: number;
    devices: Record<number, Device>;
}

export interface Device {
    id: number;
    netId: number;
    name: string;
    slug: string;
    modbusUnitId: number;
    params: Record<number, Param>;
}

export interface Param {
    id: number;
    deviceId: number;
    name: string;
    slug: string;
    fullName: string;
    address: number;
    bitPosition?: number;
    bitsAmount?: number;
    kind: string;
    isTracked: boolean;
    isProgrammable: boolean;
    label?: string;
    unitName?: string;
}

export interface Data {
    nets: Record<number, Net>;
    devices: Record<number, Device>;
    params: Record<number, Param>;
}

export class OrgStruct implements Data {
    public nets: Record<number, Net> = {};
    public devices: Record<number, Device> = {};
    public params: Record<number, Param> = {};
    public pathIdCache: Record<string, number> = {};
    public isReady = false;

    constructor(
        public readonly orgId: number,
        public readonly aliases: Record<string, string> = {},
        private readonly visNetId: number | undefined = undefined,
        private readonly visDeviceId: number | undefined = undefined,
        data?: Data,
    ) {
        if (data) {
            this.nets = data.nets;
            this.devices = data.devices;
            this.params = data.params;
            this.isReady = true;
        }
    }

    public async loadNets(): Promise<void> {
        const resp = await apiRequest<OrgDeviceParamTreeResponse>(
            `/v1/org/${this.orgId}/nets/device-param-tree/`,
            "get",
        );

        for (const netId in resp) {
            const respNet = resp[netId];
            const devices: Record<number, Device> = {};
            for (const devId in respNet.devices) {
                const respDevice = respNet.devices[devId];
                const params: Record<number, Param> = {};
                for (const paramId in respDevice.parameters) {
                    const respParam = respDevice.parameters[paramId];
                    const param: Param = {
                        id: Number(paramId),
                        deviceId: Number(devId),
                        name: respParam.name,
                        slug: respParam.slug,
                        fullName: respParam.fullName,
                        address: respParam.address,
                        bitPosition: respParam.bitPosition,
                        bitsAmount: respParam.bitsAmount,
                        kind: respParam.kind,
                        isTracked: respParam.isTracked,
                        isProgrammable: respParam.isProgrammable,
                        label: respParam.label,
                        unitName: respParam.unitName,
                    };
                    params[Number(paramId)] = param;
                    this.params[Number(paramId)] = param;
                }
                const dev: Device = {
                    id: Number(devId),
                    netId: Number(netId),
                    name: respDevice.name,
                    slug: respDevice.slug,
                    modbusUnitId: respDevice.modbusUnitId,
                    params,
                };
                devices[Number(devId)] = dev;
                this.devices[Number(devId)] = dev;
            }
            const net: Net = {
                id: Number(netId),
                name: respNet.name,
                slug: respNet.slug,
                solution: respNet.solution,
                mac: respNet.mac,
                ip: respNet.ip,
                port: respNet.port,
                devices,
            };
            this.nets[Number(netId)] = net;
        }
        this.isReady = true;
    }

    public getNetId(idMacOrIpPort: string): number | undefined {
        for (const netId in this.nets) {
            if (
                idMacOrIpPort.startsWith("#") &&
                Number(netId) === Number(idMacOrIpPort.slice(1))
            ) {
                return Number(netId);
            }
            if (this.nets[netId].mac === idMacOrIpPort) {
                return Number(netId);
            }
            if (`${this.nets[netId].ip}:${this.nets[netId].port}` === idMacOrIpPort) {
                return Number(netId);
            }
        }
        return undefined;
    }

    public getNet(idOrMacOrIpPort: string): Net | undefined {
        if (idOrMacOrIpPort in this.nets) {
            return this.nets[Number(idOrMacOrIpPort)];
        }
        const netId = this.getNetId(idOrMacOrIpPort);
        if (netId === undefined) {
            return undefined;
        }
        return this.nets[netId];
    }

    public getDeviceId(netId: number, deviceSlaveId: number): number | undefined {
        const net = this.nets[netId];
        for (const deviceId in net.devices) {
            if (net.devices[deviceId].modbusUnitId === deviceSlaveId) {
                return Number(deviceId);
            }
        }
        return undefined;
    }

    public getParamId(deviceId: number, addressAndKindBits: string): number | undefined {
        const [addressAndKind, bits] = addressAndKindBits.split(".");
        let bitPosition: number | undefined;
        let bitsAmount: number | undefined;
        if (bits) {
            const [bitPositionStr, highBitStr] = bits.split("-");
            bitPosition = Number(bitPositionStr);
            if (highBitStr) {
                bitsAmount = Number(highBitStr) - bitPosition + 1;
            }
        }

        let address: number;
        let kind: string;
        if (addressAndKind.endsWith("i")) {
            address = Number(addressAndKind.slice(0, -1));
            kind = "input";
        } else if (addressAndKind.endsWith("c")) {
            address = Number(addressAndKind.slice(0, -1));
            kind = "coil";
        } else if (addressAndKind.endsWith("d")) {
            address = Number(addressAndKind.slice(0, -1));
            kind = "discrete-input";
        } else if (addressAndKind.endsWith("h")) {
            address = Number(addressAndKind.slice(0, -1));
            kind = "holding";
        } else {
            address = Number(addressAndKind);
            kind = "holding";
        }
        const device = this.devices[deviceId];
        for (const paramId in device.params) {
            const param = device.params[paramId];
            if (
                param.address === address &&
                param.kind === kind &&
                param.bitPosition === bitPosition &&
                param.bitsAmount === bitsAmount
            ) {
                return Number(paramId);
            }
        }
        return undefined;
    }

    public getDefaultNetId(): number | undefined {
        if (this.visNetId) {
            return this.visNetId;
        }
        const defaultNet = this.aliases.defaultNet;
        if (!defaultNet) {
            return undefined;
        }
        return this.getNetId(defaultNet);
    }

    public getDefaultNet(): Net | undefined {
        let netId = this.getDefaultNetId();
        if (!netId) {
            const deviceId = this.getDefaultDeviceId();
            if (!deviceId) {
                return undefined;
            }
            const device = this.devices[deviceId];
            if (!device) {
                return undefined;
            }
            netId = device.netId;
        }
        return this.nets[netId];
    }

    private resolveNetPath(netPath: string): number | undefined {
        if (netPath) {
            if (netPath.startsWith("#")) {
                return Number(netPath.slice(1));
            }
            return this.getNetId(netPath);
        }
        return this.getDefaultNetId();
    }

    public getDefaultDeviceId(): number | undefined {
        if (this.visDeviceId) {
            return this.visDeviceId;
        }
        const defaultDevicePath = this.aliases.defaultDevice;
        if (!defaultDevicePath) {
            return undefined;
        }
        const [netPath, devicePath] = defaultDevicePath.split(">");
        const netId = this.resolveNetPath(netPath);
        if (netId === undefined) {
            return undefined;
        }
        if (devicePath.startsWith("#")) {
            return Number(devicePath.slice(1));
        }
        return this.getDeviceId(netId, Number(devicePath));
    }

    private resolveDevicePath(netPath: string, devicePath: string): number | undefined {
        if (!devicePath) {
            return this.getDefaultDeviceId();
        }
        const netId = this.resolveNetPath(netPath);
        if (netId === undefined) {
            return undefined;
        }
        if (devicePath.startsWith("#")) {
            return Number(devicePath.slice(1));
        }
        return this.getDeviceId(netId, Number(devicePath));
    }

    private resolveParamPath(
        netPath: string,
        devicePath: string,
        paramPath: string,
    ): number | undefined {
        const deviceId = this.resolveDevicePath(netPath, devicePath);
        if (deviceId === undefined) {
            return undefined;
        }
        return this.getParamId(deviceId, paramPath);
    }

    public getId(path: string): number | undefined {
        let parts = path.split(">").map((v) => v.trim());

        parts = this.insertPathAliases(parts).map((v) => v.trim());
        if (parts.length === 1) {
            return this.resolveNetPath(parts[0]);
        } else if (parts.length === 2) {
            return this.resolveDevicePath(parts[0], parts[1]);
        } else if (parts.length === 3) {
            return this.resolveParamPath(parts[0], parts[1], parts[2]);
        } else {
            return undefined;
        }
    }

    private insertPathAliases(parts: string[]): string[] {
        let foundAlias = false;
        let i = 0;
        do {
            foundAlias = false;
            // eslint-disable-next-line @typescript-eslint/no-for-in-array
            for (const i in parts) {
                if (parts[i].startsWith("~")) {
                    foundAlias = true;
                    const alias = parts[i].slice(1);
                    if (alias in this.aliases) {
                        parts[i] = this.aliases[alias];
                    } else {
                        throw new Error(`Alias "${alias}" not found`);
                    }
                }
            }
            if (foundAlias) {
                parts = parts.join(">").split(">");
            }
            i++;
        } while (foundAlias && i <= 10);
        if (i >= 10) {
            throw new Error("Alias loop detected");
        }
        return parts;
    }

    public id(path: string): number {
        if (path in this.pathIdCache) {
            return this.pathIdCache[path];
        }

        const res = this.getId(path);
        if (res === undefined) {
            throw new Error(`Object with path "${path}" not found`);
        }
        this.pathIdCache[path] = res;
        return res;
    }

    public getObjPageUrl(path: string): string {
        const parts = this.insertPathAliases(path.split(">"));
        path = parts.join(">");

        if (parts.length === 1) {
            const netSlug = this.nets[this.id(path)].slug;
            return `${getOcpBaseUrl()}/manage/network/${netSlug}/`;
        } else if (parts.length === 2) {
            const netSlug = this.nets[this.id(path.split(">")[0])].slug;
            const devSlug = this.devices[this.id(path)].slug;
            return `${getOcpBaseUrl()}/manage/device/${netSlug}/${devSlug}/`;
        } else {
            const paramId = this.id(path);
            return `${getOcpBaseUrl()}/manage/reports/readings/?paramIds=${paramId}`;
        }
    }
}

type OrgDeviceParamTreeResponse = Record<
    string,
    {
        name: string;
        slug: string;
        solution: string;
        mac?: string;
        ip: string;
        port: number;
        devices: Record<
            string,
            {
                name: string;
                slug: string;
                modbusUnitId: number;
                parameters: Record<
                    string,
                    {
                        name: string;
                        slug: string;
                        fullName: string;
                        address: number;
                        bitPosition?: number;
                        bitsAmount?: number;
                        kind: string;
                        isTracked: boolean;
                        isProgrammable: boolean;
                        label?: string;
                        unitName?: string;
                    }
                >;
            }
        >;
    }
>;

let orgStruct: OrgStruct | undefined;

export async function getOrgStruct(orgId: number): Promise<OrgStruct> {
    if (!orgStruct) {
        orgStruct = new OrgStruct(orgId);
        await orgStruct.loadNets();
    }
    return orgStruct;
}

export function getRegistersFromAliases(
    aliases: Record<string, string>,
    defaultAliases: Record<string, unknown>,
    updatePeriodSec: number,
): [storeKey: string, path: string, updatePeriodSec: number][] {
    const defaultAliasesKeys = Object.keys(defaultAliases);
    const registers: [storeKey: string, path: string, update: number][] = [];
    for (const storeKey in aliases) {
        if (storeKey === "defaultNet" || storeKey === "defaultDevice") {
            continue;
        }
        const path = aliases[storeKey];
        if (!path || !defaultAliasesKeys.includes(storeKey)) {
            continue;
        }
        registers.push([storeKey, path, updatePeriodSec]);
    }

    return registers;
}
